import { createAsyncThunk } from "@reduxjs/toolkit";
import modelService from "../../../api/services/model";

export const getCategories = createAsyncThunk(
  "model/get-categories",
  async () => {
    return await modelService.getCategories();
  }
);

export const getCollections = createAsyncThunk(
  "model/get-collections",
  async ({ page, search }) => {
    return await modelService.getCollections({ page, search });
  }
);

export const getExploreCollections = createAsyncThunk(
  "model/get-explore-collections",
  async ({ page, search, categoryId }) => {
    return await modelService.getExploreCollections({
      page,
      search,
      categoryId,
    });
  }
);

export const getUserFavorites = createAsyncThunk(
  "model/get-user-favorites",
  async ({ page, search }) => {
    return await modelService.getUserFavorites({
      page,
      search,
    });
  }
);

export const generateCollection = createAsyncThunk(
  "model/generate-collection",
  async (requestData, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      if (requestData.image) {
        formData.append(
          "image",
          requestData.image.file,
          requestData.image.file.name
        );
      }

      if (requestData.video) {
        formData.append("video", requestData.video.file);
      }

      formData.append("modelId", requestData.modelId);

      if (requestData.customPrompt && requestData.customPrompt.trim() !== "") {
        formData.append("customPrompt", requestData.customPrompt);
      }

      if (requestData.nudify) {
        formData.append("nudify", requestData.nudify);
      }

      if (requestData.resolution) {
        formData.append("resolution", requestData.resolution);
      }

      if (requestData.seed) {
        formData.append("seed", requestData.seed);
      }

      if (requestData.fps) {
        formData.append("fps", requestData.fps);
      }

      return await modelService.generateCollection(formData);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to generate collection");
    }
  }
);

export const regenerateCollection = createAsyncThunk(
  "model/regenerate-collection",
  async ({ collectionId, seed }, { rejectWithValue }) => {
    try {
      return await modelService.regenerateCollection({ collectionId, seed });
    } catch (error) {
      return rejectWithValue(error.message || "Failed to generate collection");
    }
  }
);

export const toggleToFavorite = createAsyncThunk(
  "model/toggle-to-favorite",
  async ({ collectionId }) => {
    return await modelService.toggleToFavorite({
      collectionId,
    });
  }
);

export const deleteCollection = createAsyncThunk(
  "model/delete-collection",
  async (collectionId) => {
    return await modelService.deleteCollection(collectionId);
  }
);

export const rateModel = createAsyncThunk("model/rate-model", async (data) => {
  return await modelService.rateModel(data);
});

export const getTrainModels = createAsyncThunk(
  "model/get-train-models",
  async () => {
    return await modelService.getTrainModels();
  }
);

export const getTrainModel = createAsyncThunk(
  "model/get-train-model",
  async (trainModelId) => {
    return await modelService.getTrainModel(trainModelId);
  }
);

export const createTrainModel = createAsyncThunk(
  "model/create-train-model",
  async ({ name }, { rejectWithValue }) => {
    try {
      return await modelService.createTrainModel({ name });
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create train model");
    }
  }
);

export const updateTrainModel = createAsyncThunk(
  "model/update-train-model",
  async (
    { trainModelId, name, videosUrl, imagesUrl, prompt },
    { rejectWithValue }
  ) => {
    try {
      return await modelService.updateTrainModel({
        formData: { trainModelId, name, videosUrl, imagesUrl, prompt },
        trainModelId,
      });
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update train model");
    }
  }
);

export const deleteTrainModel = createAsyncThunk(
  "model/delete-train-model",
  async (trainModelId) => {
    return await modelService.deleteTrainModel(trainModelId);
  }
);

export const fetchTopCreators = createAsyncThunk(
  "model/fetch-top-creators",
  async () => {
    return await modelService.getTopCreators();
  }
);

export const fetchDefaultGallery = createAsyncThunk(
  "model/fetch-gallery",
  async () => {
    return await modelService.getDefaultGallery();
  }
);

export const selectCoverForTrainModel = createAsyncThunk(
  "model/select-cover",
  async ({ trainModelId, collectionId, templateName }) => {
    return await modelService.selectCoverForTrainModel({
      trainModelId,
      collectionId,
      templateName,
    });
  }
);

export const submitTrainModelForReview = createAsyncThunk(
  "model/submit-review",
  async ({ trainModelId }) => {
    return await modelService.submitTrainModelForReview({
      trainModelId,
    });
  }
);
